import { createAction, props } from '@ngrx/store';
import { QueryConfig } from '@wonder-bi-front/query-engine/interfaces/query.interface';
import { AssistantPersona, ChatMessage, ConversationSummary, LlmModelOption, ThinkingLevel } from './ai-chat.types';

// ── Conversational chat actions ───────────────────────────────────────────────
export const sendMessage = createAction(
  '[AI] Send Message',
  props<{
    message: string;
    currentQueryConfig?: Record<string, unknown>;
    currentChartConfig?: Record<string, unknown>;
    context?: 'query' | 'chart';
    /** LLM to run the turn against. Only sent by users allowed to pick one. */
    model?: string;
    /** How hard the model should think on this turn. Only sent by users allowed to pick it. */
    thinkingLevel?: ThinkingLevel;
  }>()
);
export const sendMessageSuccess = createAction(
  '[AI] Send Message Success',
  props<{ conversationId: string; newMessages: ChatMessage[] }>()
);
export const sendMessageFailure = createAction(
  '[AI] Send Message Failure',
  props<{ error: unknown }>()
);
/**
 * The user pressed stop while a turn was in flight. Unsubscribes the stream (which aborts the
 * request, and with it the agent on the server) and hands the prompt back to the composer so it can
 * be sent again as it is, or reworded.
 */
export const cancelMessage = createAction('[AI] Cancel Message');
export const streamStart = createAction('[AI] Stream Start', props<{ userMessage: ChatMessage }>());
export const streamToolMessage = createAction(
  '[AI] Stream Tool Message',
  props<{ message: ChatMessage }>()
);
export const streamComplete = createAction(
  '[AI] Stream Complete',
  props<{ conversationId: string; agentMessages: ChatMessage[] }>()
);
export const streamError = createAction(
  '[AI] Stream Error',
  props<{ error: string; code?: string; detail?: string }>()
);
/**
 * Turns shown in the conversation that never reach the agent: the global assistant's own answers
 * (clarifications, refusals, where it is taking the user). Local to the view, not persisted.
 */
export const appendLocalMessages = createAction('[AI] Append Local Messages', props<{ messages: ChatMessage[] }>());
/** Drops one message from the view, e.g. a local user turn the agent is about to echo itself. */
export const removeMessage = createAction('[AI] Remove Message', props<{ id: string }>());
/**
 * Delete the current conversation (server-side + history list). The id is carried in the
 * payload because the reducer nulls `conversationId` synchronously, so the effect can no
 * longer read it from the store by the time it runs.
 */
export const clearConversation = createAction(
  '[AI] Clear Conversation',
  props<{ conversationId: string | null }>()
);

// ── Conversation history (per-user sessions) ─────────────────────────────────
export const loadConversations = createAction('[AI] Load Conversations');
export const loadConversationsSuccess = createAction(
  '[AI] Load Conversations Success',
  props<{ conversations: ConversationSummary[] }>()
);
export const loadConversationsFailure = createAction('[AI] Load Conversations Failure');
/** Start a fresh chat without deleting the current one (it stays in history). */
export const startNewConversation = createAction('[AI] Start New Conversation');
/** Load (resume) a past conversation by id. */
export const loadConversation = createAction(
  '[AI] Load Conversation',
  props<{ conversationId: string }>()
);
export const loadConversationSuccess = createAction(
  '[AI] Load Conversation Success',
  props<{
    conversationId: string;
    messages: ChatMessage[];
    model: string | null;
    thinkingLevel: ThinkingLevel | null;
    rating: number | null;
  }>()
);
export const loadConversationFailure = createAction('[AI] Load Conversation Failure');
export const applyGeneratedQueryConfig = createAction(
  '[AI] Apply Generated QueryConfig',
  props<{ queryConfig: QueryConfig }>()
);
export const applyGeneratedChartConfig = createAction(
  '[AI] Apply Generated ChartConfig',
  props<{ chartConfig: Record<string, unknown> }>()
);

// ── Deterministic one-shot chart-config generation (the "Generate chart" button) ──
export const generateChartConfig = createAction(
  '[AI] Generate ChartConfig',
  props<{ description: string; queryIds: string[] }>()
);
export const generateChartConfigSuccess = createAction(
  '[AI] Generate ChartConfig Success',
  props<{ chartConfig: Record<string, unknown>; queryIds: string[] }>()
);
export const generateChartConfigFailure = createAction(
  '[AI] Generate ChartConfig Failure',
  props<{ error: unknown }>()
);

// ── Model and thinking-level selection (internal roles: AI_MODEL_SELECT) ─────
export const loadModels = createAction('[AI] Load Models');
export const loadModelsSuccess = createAction(
  '[AI] Load Models Success',
  props<{
    models: LlmModelOption[];
    defaultModel: string;
    thinkingLevels: ThinkingLevel[];
    /** Whether the turns run on the SQL-first path (AI_SQL_FIRST on the server). */
    sqlFirst: boolean;
    /** Whether the model selector is on (AI_MODEL_SELECTOR on the server); absent reads as off. */
    selectorEnabled?: boolean;
  }>()
);
export const loadModelsFailure = createAction('[AI] Load Models Failure');
/** Pick the LLM the next turns of this conversation run against. */
export const selectModel = createAction('[AI] Select Model', props<{ model: string }>());
/** Pick how hard the model thinks on the next turns; null goes back to the provider's own default. */
export const selectThinkingLevel = createAction(
  '[AI] Select Thinking Level',
  props<{ thinkingLevel: ThinkingLevel | null }>()
);

// ── Answer feedback (the user's own verdict on a turn) ───────────────────────
/** Score the whole session, 1-5, once it is finished. Replaces the per-turn thumb (WB-121). */
export const rateSession = createAction('[AI] Rate Session', props<{ rating: number }>());
export const rateSessionFailure = createAction('[AI] Rate Session Failure');

// ── Suggestions ───────────────────────────────────────────────────────────────
export const loadSuggestions = createAction('[AI] Load Suggestions', props<{ agent: AssistantPersona }>());
export const loadSuggestionsSuccess = createAction(
  '[AI] Load Suggestions Success',
  props<{ suggestions: string[] }>()
);
export const loadSuggestionsFailure = createAction('[AI] Load Suggestions Failure');

// ── Cross-cutting ─────────────────────────────────────────────────────────────
export const httpResult = createAction(
  '[AI] HTTP Result',
  props<{ action: unknown; error?: unknown }>()
);

export * as AIActions from './ai.actions';
